import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Header from '../components/Header';
import { useApi } from '../hooks/useApi';
import { FISCAL_IVA_OPTIONS, formatCuitInput, validateFiscalProfileForm } from '../constants/fiscalProfile';
import { updateTenantFiscalProfile } from '../common/tenantApi';
import './Settings.css';

const MY_TENANT_QUERY = `
  query MyTenantFiscal {
    myTenant {
      tenant_id
      name
      fiscal_cuit
      fiscal_legal_name
      fiscal_iva_condition
      fiscal_profile_completed_at
      my_role
    }
  }
`;

export default function FiscalProfilePage() {
  const { callApi } = useApi();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tenant, setTenant] = useState(null);
  const [fiscalCuit, setFiscalCuit] = useState('');
  const [fiscalLegalName, setFiscalLegalName] = useState('');
  const [fiscalIvaCondition, setFiscalIvaCondition] = useState('RI');

  const loadTenant = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await callApi(MY_TENANT_QUERY);
      const row = data.myTenant;
      setTenant(row);
      setFiscalCuit(formatCuitInput(row?.fiscal_cuit || ''));
      setFiscalLegalName(row?.fiscal_legal_name || '');
      setFiscalIvaCondition(row?.fiscal_iva_condition || 'RI');
    } catch (e) {
      setError(e.message || 'No se pudo cargar el perfil fiscal');
    } finally {
      setLoading(false);
    }
  }, [callApi]);

  useEffect(() => {
    loadTenant();
  }, [loadTenant]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      if ((tenant?.my_role || '').toLowerCase() !== 'admin') {
        throw new Error('Solo administradores pueden editar datos fiscales');
      }
      const validationError = validateFiscalProfileForm({
        fiscalCuit,
        fiscalLegalName,
        fiscalIvaCondition,
      });
      if (validationError) {
        throw new Error(validationError);
      }
      const updated = await updateTenantFiscalProfile({
        fiscal_cuit: fiscalCuit,
        fiscal_legal_name: fiscalLegalName.trim(),
        fiscal_iva_condition: fiscalIvaCondition,
      });
      setTenant(updated);
      setSuccess('Datos fiscales guardados');
    } catch (e) {
      setError(e.message || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Header />
      <div className="settings-container">
        <div className="settings-header">
          <RouterLink to="/settings" className="ob-link-back">← Volver a Settings</RouterLink>
          <h1>Datos fiscales</h1>
          <p className="ob-step-desc">
            CUIT, razón social y condición ante IVA de tu organización (datos requeridos del perfil
            de la empresa). Se usan para identificar tu empresa como receptor en facturas de
            proveedores y mejorar la extracción automática.
            Al guardar, actualizamos el prompt de facturas de tu workspace con estos datos.
          </p>
        </div>

        {loading ? (
          <p>Cargando…</p>
        ) : (
          <form className="fiscal-profile-form" onSubmit={handleSubmit}>
            <div className="ob-field">
              <label htmlFor="fiscal-cuit">CUIT</label>
              <input
                id="fiscal-cuit"
                value={fiscalCuit}
                onChange={(e) => setFiscalCuit(formatCuitInput(e.target.value))}
                placeholder="20-27009484-9"
                inputMode="numeric"
                required
              />
            </div>
            <div className="ob-field">
              <label htmlFor="fiscal-name">Razón social</label>
              <input
                id="fiscal-name"
                value={fiscalLegalName}
                onChange={(e) => setFiscalLegalName(e.target.value)}
                placeholder="Mi Empresa SRL"
                required
              />
            </div>
            <div className="ob-field">
              <label htmlFor="fiscal-iva">Condición ante IVA</label>
              <select
                id="fiscal-iva"
                value={fiscalIvaCondition}
                onChange={(e) => setFiscalIvaCondition(e.target.value)}
                required
              >
                {FISCAL_IVA_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {tenant?.fiscal_profile_completed_at && (
              <p className="fiscal-profile-meta">
                Perfil completado {new Date(tenant.fiscal_profile_completed_at).toLocaleString('es-AR')}
              </p>
            )}

            {error && <p className="ob-error">{error}</p>}
            {success && <p className="ob-success">{success}</p>}

            <button type="submit" className="ob-btn-primary" disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar datos fiscales'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
